/**
 * Authentication utility functions
 * Stores JWT tokens in localStorage (remember me = 48 hours) or sessionStorage (session-only).
 */

const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const GUEST_TOKEN_KEY = 'agent_guest_token_v1'

/** Which storage currently holds tokens: sessionStorage first (session login), else localStorage (remember me). */
function getTokenStorage() {
  try {
    if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) return sessionStorage
    return localStorage
  } catch {
    return localStorage
  }
}

/**
 * Get the stored access token (sessionStorage first, then localStorage)
 * @returns {string|null} The access token or null if not found
 */
export function getAccessToken() {
  try {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY) || localStorage.getItem(ACCESS_TOKEN_KEY)
  } catch (e) {
    console.error('Error getting access token:', e)
    return null
  }
}

/**
 * Store the access token in the same storage that currently holds tokens (for refresh flow)
 * @param {string} token - The access token to store
 */
export function setAccessToken(token) {
  const storage = getTokenStorage()
  try {
    if (token) {
      storage.setItem(ACCESS_TOKEN_KEY, token)
    } else {
      storage.removeItem(ACCESS_TOKEN_KEY)
    }
  } catch (e) {
    console.error('Error setting access token:', e)
  }
}

/**
 * Get the stored refresh token
 * @returns {string|null} The refresh token or null if not found
 */
export function getRefreshToken() {
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch (e) {
    console.error('Error getting refresh token:', e)
    return null
  }
}

/**
 * Store the refresh token in the same storage that currently holds tokens
 * @param {string} token - The refresh token to store
 */
export function setRefreshToken(token) {
  const storage = getTokenStorage()
  try {
    if (token) {
      storage.setItem(REFRESH_TOKEN_KEY, token)
    } else {
      storage.removeItem(REFRESH_TOKEN_KEY)
    }
  } catch (e) {
    console.error('Error setting refresh token:', e)
  }
}

/**
 * Clear all stored tokens from both sessionStorage and localStorage
 */
export function clearTokens() {
  try {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  } catch (e) {
    console.error('Error clearing tokens:', e)
  }
}

/**
 * Get authorization headers for fetch requests
 * @returns {Object} Headers object with Authorization header if token exists
 */
export function getAuthHeaders() {
  const token = getAccessToken()
  if (token) {
    return {
      'Authorization': `Bearer ${token}`
    }
  }
  try {
    const guestToken = localStorage.getItem(GUEST_TOKEN_KEY)
    if (guestToken) return { 'X-Guest-Token': guestToken }
  } catch {
    // Ignore unavailable storage.
  }
  return {}
}

/**
 * Create fetch options with authentication headers
 * @param {Object} options - Fetch options to merge with auth headers
 * @returns {Object} Fetch options with Authorization header included
 */
export function withAuth(options = {}) {
  const authHeaders = getAuthHeaders()
  return {
    ...options,
    headers: {
      ...authHeaders,
      ...(options.headers || {})
    }
  }
}

/**
 * Login and store tokens
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {boolean} [rememberMe=true] - If true, store in localStorage (48-hour refresh) and set cookie; if false, sessionStorage only (session-style)
 * @returns {Promise<Object>} Login response with tokens
 */
export async function login(email, password, rememberMe = true) {
  try {
    const response = await fetch('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, remember_me: rememberMe })
    })

    // Check content type first
    const contentType = response.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')
    
    // Read response as text first (can only read body once)
    const text = await response.text()
    
    // If response is empty, provide helpful error
    if (!text || !text.trim()) {
      if (response.ok) {
        throw new Error('Server returned empty response. The backend may not be running correctly or the login endpoint may have an issue.')
      } else {
        throw new Error(`Server returned error ${response.status} ${response.statusText} with no error message. Please check the backend logs.`)
      }
    }
    
    let data = {}
    
    // Try to parse as JSON - assume JSON for API endpoints even if content-type is missing or empty
    // (some proxies or servers might not set content-type correctly)
    const shouldTryJson = isJson || !contentType || contentType.trim() === ''
    
    if (shouldTryJson) {
      try {
        data = JSON.parse(text)
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError, 'Content-Type:', contentType, 'Response text:', text.substring(0, 200))
        // If it looks like HTML, provide helpful error
        if (text.includes('<!doctype') || text.includes('<html') || text.trim().startsWith('<')) {
          throw new Error('Received HTML instead of JSON (often a 502 from the reverse proxy). In production: ensure the backend container is running and the proxy forwards /auth to the backend on port 8000. See docs/guides/troubleshooting/PRODUCTION_TROUBLESHOOTING.md.')
        }
        throw new Error(`Invalid JSON response from server. Content-Type: "${contentType || '(not set)'}". Response: ${text.substring(0, 200)}`)
      }
    } else {
      // Non-JSON response (likely HTML error page)
      if (text.includes('<!doctype') || text.includes('<html') || text.trim().startsWith('<')) {
        throw new Error('Received HTML instead of JSON (often a 502 from the reverse proxy). In production: ensure the backend container is running and the proxy forwards /auth to the backend on port 8000. See docs/guides/troubleshooting/PRODUCTION_TROUBLESHOOTING.md.')
      } else {
        throw new Error(`Server returned unexpected content type: "${contentType}". Expected JSON. Response: ${text.substring(0, 200)}`)
      }
    }

    if (!response.ok) {
      const detailObj = data && typeof data.detail === 'object' ? data.detail : null
      // Check if this is a validation error with field-specific details
      if (data.context && data.context.validation_errors && Array.isArray(data.context.validation_errors)) {
        // Create a validation error object that can be handled by the component
        const validationError = new Error(data.message || data.detail || 'Validation failed')
        validationError.validationErrors = data.context.validation_errors
        validationError.errorCode = data.error_code
        throw validationError
      }
      
      // Extract error message from structured error response
      const errorMsg =
        data.user_message ||
        detailObj?.user_message ||
        detailObj?.message ||
        data.message ||
        (typeof data.detail === 'string' ? data.detail : '') ||
        data.error_code ||
        `HTTP ${response.status}: ${response.statusText}`
      throw new Error(errorMsg)
    }

    // Store tokens in chosen storage: remember me = localStorage (48 hours); else sessionStorage (session-only)
    const storage = rememberMe ? localStorage : sessionStorage
    const other = rememberMe ? sessionStorage : localStorage
    if (data.access_token) {
      storage.setItem(ACCESS_TOKEN_KEY, data.access_token)
      other.removeItem(ACCESS_TOKEN_KEY)
    }
    if (data.refresh_token) {
      storage.setItem(REFRESH_TOKEN_KEY, data.refresh_token)
      other.removeItem(REFRESH_TOKEN_KEY)
    }
    return data
  } catch (error) {
    console.error('Login error:', error)
    throw error
  }
}

/**
 * Logout and clear tokens
 */
export function logout() {
  clearTokens()
}

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    console.warn('No refresh token available')
    return null
  }

  try {
    const response = await fetch('/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    })

    if (!response.ok) {
      console.warn('Token refresh failed:', response.status, response.statusText)
      return null
    }

    const data = await response.json()
    
    if (data.access_token) {
      setAccessToken(data.access_token)
      if (data.refresh_token) {
        setRefreshToken(data.refresh_token)
      }
      return data.access_token
    }

    return null
  } catch (error) {
    console.error('Error refreshing token:', error)
    return null
  }
}

// Track ongoing refresh to prevent multiple simultaneous refresh attempts
let refreshPromise = null

/**
 * Browser-local calendar date (YYYY-MM-DD) for server-side "past slot" checks.
 * Keeps meal-plan edits aligned with the user's timezone when the server runs in UTC.
 */
export function getClientLocalDateHeader() {
  const now = new Date()
  const y = now.getFullYear()
  const mo = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/**
 * Authenticated fetch wrapper that automatically refreshes tokens on 401/403 errors
 * @param {string|Request} url - URL or Request object
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function authenticatedFetch(url, options = {}) {
  // Get current token and add to headers
  const token = getAccessToken()
  const headers = {
    'X-Client-Local-Date': getClientLocalDateHeader(),
    ...(options.headers || {}),
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  } else {
    try {
      const guestToken = localStorage.getItem(GUEST_TOKEN_KEY)
      if (guestToken) headers['X-Guest-Token'] = guestToken
    } catch {
      // Storage may be unavailable; the request will return its normal auth response.
    }
  }

  // Make initial request
  let response = await fetch(url, {
    ...options,
    headers
  })

  // If we get a 401 or 403, try to refresh the token and retry
  // 401 = Unauthorized (token missing/invalid)
  // 403 = Forbidden (token expired or insufficient permissions - often means expired token)
  // Attempt refresh whenever we have a refresh token, even if access token is missing.
  // This avoids a "logged in but no Authorization header" state if access_token was evicted
  // (storage quirks, partial clears, or older clients) while refresh_token remains.
  if ((response.status === 401 || response.status === 403)) {
    const refreshToken = getRefreshToken()
    
    // Only try to refresh if we have a refresh token available
    // If no refresh token, user is logged out - don't attempt refresh
    if (!refreshToken) {
      console.log(`Received ${response.status} but no refresh token available (user logged out)`)
      if (token) window.dispatchEvent(new CustomEvent('tokenRefreshFailed'))
      return response
    }
    
    console.log(`Received ${response.status}, attempting to refresh token...`)
    
    // Use existing refresh promise if one is in progress (prevent race conditions)
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken()
    }
    
    const newToken = await refreshPromise
    refreshPromise = null // Clear after use
    
    if (newToken) {
      console.log('Token refreshed successfully, retrying request...')
      // Retry the request with the new token
      headers['Authorization'] = `Bearer ${newToken}`
      response = await fetch(url, {
        ...options,
        headers
      })
    } else {
      console.warn(`Token refresh failed, request will return ${response.status}`)
      // If refresh failed, dispatch logout event to clear auth state
      window.dispatchEvent(new CustomEvent('tokenRefreshFailed'))
    }
  }

  return response
}

